/**
 * The per-transaction processing rate CPOS charges merchants.
 *
 * One rate on every plan. Free and Pro used to differ here, and the surfaces
 * that quoted them drifted apart; this is the single figure every app renders
 * so they cannot disagree again. What a merchant is actually billed comes from
 * Stripe — the rate is attached to their connected account's pricing group —
 * so this states what we advertise, not what was charged. Read the recorded
 * fee on a transaction when reporting a real amount.
 *
 * Formatting stays with the caller: the web quotes "2.49% + $0.10" and the
 * mobile apps "2.49% + 10¢", which is presentation rather than pricing.
 */
export const TAP_RATE_BPS = 249;
export const TAP_RATE_PERCENT = TAP_RATE_BPS / 100;
export const TAP_RATE_FIXED_CENTS = 10;

/**
 * `bps` basis points of `cents`, rounded to the nearest cent.
 *
 * Integer rate units keep every fee exact until the one final rounding: a
 * fractional percentage such as 2.99 has no exact binary form, so multiplying
 * by it can round a cent the wrong way.
 */
export function percentOfCents(cents: number, bps: number): number {
  return Math.round((cents * bps) / 10_000);
}

/** The rate as web-facing copy: "2.49% + $0.10". */
export const TAP_RATE_LABEL = `${TAP_RATE_PERCENT}% + $${(
  TAP_RATE_FIXED_CENTS / 100
).toFixed(2)}`;

/**
 * The merchant's processing rate on a Freepay card sale, and the card fee
 * Freepay adds to the customer's total.
 *
 * One platform rate for every merchant, not a per-merchant setting: changing
 * it is a code change and a release, which is also when the attestation and
 * pricing copy that quote it change. The two figures are the same by design,
 * so the customer's fee never exceeds what the merchant pays to accept the
 * card. The difference from the tap rate is CPOS platform revenue. Cash sales,
 * and every sale with Freepay off, stay at the tap rate.
 */
export const FREEPAY_RATE_BPS = 299;
export const FREEPAY_RATE_PERCENT = FREEPAY_RATE_BPS / 100;
export const FREEPAY_RATE_FIXED_CENTS = 10;

/** The CPOS share of a Freepay sale's fee: the Freepay rate minus the tap rate. */
export const FREEPAY_PLATFORM_SHARE_PERCENT =
  (FREEPAY_RATE_BPS - TAP_RATE_BPS) / 100;

/** The Freepay rate as web-facing copy: "2.99% + $0.10". */
export const FREEPAY_RATE_LABEL = `${FREEPAY_RATE_PERCENT}% + $${(
  FREEPAY_RATE_FIXED_CENTS / 100
).toFixed(2)}`;

/**
 * The Freepay card fee inside a recorded sale, in cents, or 0 when the sale
 * was not a Freepay sale.
 *
 * Nothing on a transaction records that it was a Freepay sale, so the fee
 * tells: a tap sale is charged the tap rate on its full amount, while a
 * Freepay sale is charged the Freepay rate on its amount before the card fee
 * and the tip (that same figure is the card fee). Freepay sales made before
 * the tip was exempted also carry the tap percentage on the tip, and read the
 * same. A fee matching neither, or matching both on a tiny sale, reads as 0.
 *
 * @param amountCents - The full amount charged, tip included.
 * @param applicationFeeCents - The fee Stripe recorded for the sale.
 * @param tipCents - The tip within `amountCents`.
 */
export function freepayCardFeeCents(
  amountCents: number,
  applicationFeeCents: number | null | undefined,
  tipCents = 0,
): number {
  if (!applicationFeeCents || applicationFeeCents <= 0) return 0;
  if (
    applicationFeeCents ===
    percentOfCents(amountCents, TAP_RATE_BPS) + TAP_RATE_FIXED_CENTS
  ) {
    return 0;
  }
  const candidates = [
    applicationFeeCents,
    applicationFeeCents - percentOfCents(tipCents, TAP_RATE_BPS),
  ];
  return (
    candidates.find(
      (cardFeeCents) =>
        cardFeeCents ===
        percentOfCents(
          amountCents - tipCents - cardFeeCents,
          FREEPAY_RATE_BPS,
        ) +
          FREEPAY_RATE_FIXED_CENTS,
    ) ?? 0
  );
}
